import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import { EditorState, Compartment, type Extension } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, highlightActiveLine, highlightActiveLineGutter, drawSelection, rectangularSelection, placeholder as cmPlaceholder } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab, toggleComment } from '@codemirror/commands';
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { autocompletion, closeBrackets, closeBracketsKeymap, completionKeymap } from '@codemirror/autocomplete';
import { bracketMatching, indentOnInput } from '@codemirror/language';
import { linter, lintGutter, type Diagnostic } from '@codemirror/lint';
import { sql, PostgreSQL } from '@codemirror/lang-sql';
import type { QueryError } from '@/types/api';
import { workbenchTheme } from './theme';
import { createCatalogCompletion, type CatalogSnapshot } from './catalogCompletion';

export interface SqlEditorHandle {
  focus: () => void;
  getSelection: () => string;
  getValue: () => string;
  insertAtCursor: (text: string) => void;
  setValue: (text: string) => void;
}

export interface SqlEditorProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onRun: () => void;
  readonly onRunSelection: () => void;
  readonly onRunAndAdvance: () => void;
  readonly getCatalog: () => CatalogSnapshot;
  /** Backend error; its `position` becomes an inline squiggle. */
  readonly error: QueryError | null;
  readonly placeholder?: string;
  readonly ariaLabel: string;
}

/**
 * CodeMirror 6 SQL editor.
 *
 * Run handlers are held in a ref and read through a stable keymap, so changing
 * a callback never tears down and rebuilds the editor state (which would lose
 * undo history and the cursor).
 */
export const SqlEditor = forwardRef<SqlEditorHandle, SqlEditorProps>(function SqlEditor(
  { value, onChange, onRun, onRunSelection, onRunAndAdvance, getCatalog, error, placeholder, ariaLabel },
  ref,
) {
  const hostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const diagnosticsRef = useRef<Diagnostic[]>([]);
  const lintCompartment = useRef(new Compartment());

  const handlers = useRef({ onChange, onRun, onRunSelection, onRunAndAdvance, getCatalog });
  handlers.current = { onChange, onRun, onRunSelection, onRunAndAdvance, getCatalog };

  useImperativeHandle(
    ref,
    () => ({
      focus: () => viewRef.current?.focus(),
      getValue: () => viewRef.current?.state.doc.toString() ?? '',
      getSelection: () => {
        const view = viewRef.current;
        if (!view) return '';
        const { from, to } = view.state.selection.main;
        return from === to ? '' : view.state.sliceDoc(from, to);
      },
      insertAtCursor: (text: string) => {
        const view = viewRef.current;
        if (!view) return;
        const { from, to } = view.state.selection.main;
        view.dispatch({
          changes: { from, to, insert: text },
          selection: { anchor: from + text.length },
        });
        view.focus();
      },
      setValue: (text: string) => {
        const view = viewRef.current;
        if (!view) return;
        view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: text } });
      },
    }),
    [],
  );

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    const runKeymap = keymap.of([
      { key: 'Mod-Enter', preventDefault: true, run: () => { handlers.current.onRun(); return true; } },
      { key: 'Mod-Shift-Enter', preventDefault: true, run: () => { handlers.current.onRunSelection(); return true; } },
      { key: 'Shift-Enter', preventDefault: true, run: () => { handlers.current.onRunAndAdvance(); return true; } },
      { key: 'Mod-/', preventDefault: true, run: toggleComment },
    ]);

    const extensions: Extension[] = [
      lineNumbers(),
      highlightActiveLine(),
      highlightActiveLineGutter(),
      history(),
      drawSelection(),
      rectangularSelection(),
      indentOnInput(),
      bracketMatching(),
      closeBrackets(),
      highlightSelectionMatches(),
      lintGutter(),
      sql({ dialect: PostgreSQL, upperCaseKeywords: false }),
      autocompletion({
        override: [createCatalogCompletion(() => handlers.current.getCatalog())],
        activateOnTyping: true,
        icons: false,
        maxRenderedOptions: 40,
      }),
      // Run bindings come first so Mod-Enter is never swallowed by a default.
      runKeymap,
      keymap.of([...closeBracketsKeymap, ...completionKeymap, ...searchKeymap, ...historyKeymap, ...defaultKeymap, indentWithTab]),
      workbenchTheme(),
      EditorView.lineWrapping,
      EditorView.contentAttributes.of({ 'aria-label': ariaLabel }),
      lintCompartment.current.of(linter(() => diagnosticsRef.current, { delay: 50 })),
      EditorView.updateListener.of((update) => {
        if (update.docChanged) handlers.current.onChange(update.state.doc.toString());
      }),
    ];

    if (placeholder) extensions.push(cmPlaceholder(placeholder));

    const view = new EditorView({
      state: EditorState.create({ doc: value, extensions }),
      parent: host,
    });
    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // Mounted once. Prop changes are handled by the effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Adopt external edits (command palette, object explorer inserts, history re-run).
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    if (view.state.doc.toString() === value) return;
    view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: value } });
  }, [value]);

  // Backend error position -> inline squiggle.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    if (!error) {
      diagnosticsRef.current = [];
    } else {
      const length = view.state.doc.length;
      const from =
        typeof error.position === 'number' && error.position >= 0
          ? Math.min(error.position, Math.max(0, length - 1))
          : 0;
      // Underline to the end of the offending token, not just one character.
      const text = view.state.doc.sliceString(from, Math.min(length, from + 80));
      const tokenLength = /^[\w$."]+/.exec(text)?.[0]?.length ?? 1;

      diagnosticsRef.current = [
        {
          from,
          to: Math.min(length, from + Math.max(1, tokenLength)),
          severity: 'error',
          message: `${error.message}\nSQLSTATE ${error.sqlState}`,
          source: 'duckling',
        },
      ];
    }

    view.dispatch({
      effects: lintCompartment.current.reconfigure(
        linter(() => diagnosticsRef.current, { delay: 50 }),
      ),
    });
  }, [error]);

  return <div ref={hostRef} className="min-h-0 w-full" data-testid="sql-editor" />;
});
